import React from "react";
import { ImageComponent } from "./ImageComponent.js";
import { useBlockData, useYooptaEditor, useBlockSelected, useYooptaReadOnly, Elements } from "@yoopta/editor";
import { Resizable } from "re-resizable";
import { useEffect, useMemo, useState } from "react";
import { Placeholder } from "./Placeholder.js";
import { ImageBlockOptions } from "./ImageBlockOptions.js";
import { Resizer } from "./Resizer.js";

const ImageRender = ({ extendRender, ...props }) => {
  const { element, blockId, children, attributes } = props;
  const { src, alt, bgColor, fit, sizes: propSizes } = element.props || {};
  const blockData = useBlockData(blockId);
  const editor = useYooptaEditor();
  const isReadOnly = useYooptaReadOnly();

  const [sizes, setSizes] = useState({
    width: propSizes?.width || 650,
    height: propSizes?.height || 440,
  });

  useEffect(
    () =>
      setSizes({
        width: propSizes?.width || 650,
        height: propSizes?.height || 440,
      }),
    [element.props, propSizes?.height, propSizes?.width]
  );

  const blockSelected = useBlockSelected({ blockId });

  const resizeProps = useMemo(
    () => ({
      minWidth: 300,
      size: { width: sizes.width, height: sizes.height },
      maxWidth: "100%",
      lockAspectRatio: true,
      resizeRatio: 2,
      enable: {
        left: !isReadOnly,
        right: !isReadOnly,
      },
      handleStyles: {
        left: { left: 0 },
        right: { right: 0 },
      },
      onResize: (_e, _direction, ref) => {
        if (isReadOnly) return;
        setSizes({ width: ref.offsetWidth, height: ref.offsetHeight });
      },
      onResizeStop: (_e, _direction, ref) => {
        if (isReadOnly) return;
        Elements.updateElement(editor, blockId, {
          type: "image",
          props: {
            sizes: { width: ref.offsetWidth, height: ref.offsetHeight },
          },
        });
      },
      handleComponent: {
        left: isReadOnly ? <></> : <Resizer position="left" />,
        right: isReadOnly ? <></> : <Resizer position="right" />,
      },
    }),
    [sizes.width, sizes.height, blockId, editor, isReadOnly]
  );

  if (!src) {
    if (isReadOnly) return <></>;

    return (
      <Placeholder attributes={attributes} blockId={blockId}>
        {children}
      </Placeholder>
    );
  }

  const currentAlign = blockData?.meta?.align || "center";
  const alignClass = `yoopta-align-${currentAlign}`;

  return (
    <div
      contentEditable={false}
      draggable={false}
      className={`yoo-image-mt-4 yoo-image-relative yoo-image-flex ${alignClass} yoopta-image`}
      style={{ maxWidth: "100%", overflow: "hidden" }}
    >
      <Resizable {...resizeProps} className="yoo-image-my-0 yoo-image-flex" style={{ maxWidth: "100%" }}>
        {blockSelected && (
          <div className="yoo-image-absolute yoo-image-pointer-events-none yoo-image-inset-0 yoo-image-bg-[rgba(35,131,226,0.14)] yoo-image-z-[81] yoo-image-rounded-[3px] yoo-image-opacity-100 yoo-image-transition-opacity yoo-image-duration-150 yoo-image-ease-in" />
        )}
        {extendRender ? (
          extendRender(props)
        ) : (
          <ImageComponent
            src={src}
            alt={alt}
            fit={fit}
            width={sizes?.width}
            height={sizes?.height}
            bgColor={bgColor}
            attributes={attributes}
          >
            {children}
          </ImageComponent>
        )}
        {!isReadOnly && <ImageBlockOptions block={blockData} editor={editor} props={element.props} />}
      </Resizable>
    </div>
  );
};

export { ImageRender };
